import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertCircle, Calendar } from "lucide-react";

interface CallSetupStepProps {
  callNotes: string;
  onNotesChange: (notes: string) => void;
  scheduledDate: string;
  onDateChange: (date: string) => void;
  selectedLeadCount: number;
  error?: string;
}

export function CallSetupStep({
  callNotes,
  onNotesChange,
  scheduledDate,
  onDateChange,
  selectedLeadCount,
  error
}: CallSetupStepProps) {
  const features = [
    { title: "Real-time Transcription", desc: "Every word captured instantly" },
    { title: "Objection Detection", desc: "AI identifies concerns automatically" },
    { title: "Suggested Responses", desc: "Get winning replies in real-time" },
    { title: "Sentiment Analysis", desc: "Track call mood and engagement" },
  ];

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        Schedule AI-assisted calls for {selectedLeadCount} selected leads. AI provides real-time support during calls.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        {features.map(feature => (
          <Card key={feature.title}>
            <CardContent className="p-4">
              <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
              <div className="font-medium text-sm">{feature.title}</div>
              <div className="text-xs text-muted-foreground">{feature.desc}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="scheduledDate">Schedule Calls For</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="scheduledDate"
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => onDateChange(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="callNotes">Call Preparation Notes</Label>
          <Textarea
            id="callNotes"
            placeholder="Add talking points, objections to prepare for, key questions to ask..."
            value={callNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}
